import { ReactNode } from "react";
import { Link, useLocation } from "@/lib/react-router-dom-shim";
import { useTranslation } from "react-i18next";
import { FileText, Shield, ChevronRight } from "lucide-react";

interface LegalPageLayoutProps {
  title: string;
  lastUpdate?: string;
  children: ReactNode;
}

export function LegalPageLayout({ title, lastUpdate, children }: LegalPageLayoutProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const legalPages = [
    {
      path: "/terminos-y-condiciones",
      label: t('terms.breadcrumb', 'Termos e Condições'),
      icon: FileText
    },
    {
      path: "/politica-de-privacidad",
      label: t('privacy.breadcrumb', 'Política de Privacidade'),
      icon: Shield
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="bg-primary/5 border-b border-border">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
            {title}
          </h1>
          {lastUpdate && (
            <p className="text-muted-foreground mt-4">
              {lastUpdate}
            </p>
          )}
        </div>
      </section>

      {/* Content Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Sidebar */}
          <aside className="lg:col-span-3">
            <div className="sticky top-24">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                {t('legal.navigation', 'Páginas Legais')}
              </h3>
              <nav className="space-y-1">
                {legalPages.map((page) => {
                  const isActive = location.pathname === page.path;
                  const Icon = page.icon;
                  return (
                    <Link
                      key={page.path}
                      to={page.path}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                        ${isActive 
                          ? 'bg-primary text-primary-foreground font-medium' 
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }
                      `}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="flex-1">{page.label}</span>
                      {isActive && <ChevronRight className="h-4 w-4" />}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <article className="lg:col-span-9">
            <div className="prose prose-slate dark:prose-invert max-w-none">
              {children}
            </div>
          </article>
        </div>
      </div>
    </>
  );
}